import { useState } from 'react'
import { formatDateTime } from '@/utils/date'
import { Button } from '@/components/ui/button'
import { OwnerPicker } from '@/pages/admin/OwnerPicker'
import type { AiOwner } from '@/pages/admin/OwnerPicker'
import { DataMapPanel } from '@/pages/admin/DataMapPanel'
import type { DataMap } from '@/pages/admin/DataMapPanel'

// One row of the AI Register table (AI-REGISTER-1B, badges and review line
// AI-REGISTER-2). Display only: the section owns the drafts and the Save call.

export interface RegisterFeature {
  key: string
  name: string
  models: string[]
  model_override_env: string | null
  calls: number
  cost_usd: number
  last_used_at: string | null
  models_seen: string[]
  model_mismatch: boolean
  switched_off: boolean
  owner: string | null
  risk: string | null
  reviewed_at?: string | null
  reviewed_by?: string | null
  review_due_at?: string | null
  attention?: string[]
  data_map?: DataMap
}

export interface Draft { owner: string; risk: string }

const RISKS = [
  { value: '', label: 'Not set' },
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
]

const TONES = {
  gray: 'bg-gray-200 text-gray-800',
  amber: 'bg-amber-100 text-amber-800',
  blue: 'bg-blue-100 text-blue-800',
}

export const fmtDate = (iso: string | null) => (iso ? formatDateTime(iso) : 'never')

function Badge({ tone, children }: { tone: keyof typeof TONES; children: React.ReactNode }) {
  return <span className={`inline-flex rounded px-2 py-0.5 text-xs font-medium ${TONES[tone]}`}>{children}</span>
}

interface RowProps {
  f: RegisterFeature
  draft: Draft
  error?: string
  owners: AiOwner[]
  onEdit: (key: string, patch: Partial<Draft>) => void
  onSave: (key: string) => void
}

export function AiRegisterRow({ f, draft: d, error: err, owners, onEdit, onSave }: RowProps) {
  const attention = f.attention ?? []
  const [mapOpen, setMapOpen] = useState(false)
  return (
    <>
    <tr data-testid={`register-row-${f.key}`} className="border-t align-top">
      <td className="py-2 pr-2">
        {f.name}
        <span className="block text-xs text-gray-400">{f.key}</span>
        <div className="mt-1 flex flex-wrap gap-1">
          {f.switched_off && <Badge tone="gray">Off</Badge>}
          {f.model_mismatch && <Badge tone="amber">Model differs: {f.models_seen.join(', ')}</Badge>}
          {f.model_override_env && <Badge tone="blue">Model can be changed by setting {f.model_override_env}</Badge>}
          {!f.risk && <Badge tone="amber">Risk not set</Badge>}
          {attention.includes('owner_missing') && <Badge tone="amber">Owner missing</Badge>}
          {attention.includes('review_overdue') && <Badge tone="amber">Review overdue</Badge>}
        </div>
        <Button type="button" size="sm" variant="outline" className="mt-1" aria-expanded={mapOpen}
          data-testid={`register-datamap-toggle-${f.key}`} onClick={() => setMapOpen(o => !o)}>
          {mapOpen ? 'Hide data map' : 'Data map'}
        </Button>
      </td>
      <td className="py-2 pr-2 text-xs">{f.models.join(', ')}</td>
      <td className="py-2 pr-2">{f.calls}</td>
      <td className="py-2 pr-2">{f.cost_usd.toFixed(4)}</td>
      <td className="py-2 pr-2">{fmtDate(f.last_used_at)}</td>
      <td className="py-2 pr-2">
        <OwnerPicker allowBlank owners={owners} testId={`register-owner-${f.key}`} value={d.owner}
          onChange={owner => onEdit(f.key, { owner })} />
        {err && <span data-testid={`register-error-${f.key}`} className="mt-1 block text-sm text-red-600">{err}</span>}
      </td>
      <td className="py-2 pr-2">
        <select data-testid={`register-risk-${f.key}`} className="rounded border px-2 py-1"
          value={d.risk} onChange={e => onEdit(f.key, { risk: e.target.value })}>
          {RISKS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
        </select>
        {f.reviewed_at && (
          <span className="mt-1 block text-xs text-gray-500">
            Reviewed {formatDateTime(f.reviewed_at)} by {f.reviewed_by}
          </span>
        )}
      </td>
      <td className="py-2">
        <Button type="button" size="sm" data-testid={`register-save-${f.key}`} onClick={() => onSave(f.key)}>Save</Button>
      </td>
    </tr>
    {mapOpen && (
      <tr className="bg-gray-50">
        <td colSpan={7} className="px-2 py-2"><DataMapPanel fkey={f.key} map={f.data_map} /></td>
      </tr>
    )}
    </>
  )
}
